<template>
  <bm-control
    :t="t"
    :rules="rules"
    :read-only="readOnly"
    :read-only-value="readOnlyValue"
    :label="label"
    :tooltip="tooltip"
    :router-location="routerLocation"
    :data-qa="dataQa"
  >
    <template #default="{ dataQa: dataQaFromControl }">
      <component
        :is="componentType"
        :placeholder="placeholderVal"
        :filter="customFilter"
        color="brand02"
        :items="sortedSelectableItems"
        :item-text="itemPropertyTextBinding"
        :item-value="extractPrimitiveValue"
        :value="primitiveValue"
        :label="undefined"
        :data-qa="dataQaFromControl"
        :data-component="dataComponent"
        :return-object="false"
        :item-order="itemOrder"
        :attach="attach"
        :disabled="disabled"
        :multiple="multiple"
        :clearable="clearable"
        :loading="loading"
        :hide-details="hideDetails"
        :auto-select-first="autoSelectFirst"
        :autocomplete="autocomplete"
        :class="classFromAttrs"
        :error-messages="errorMessages"
        :autofocus="autofocus"
        :append-outer-icon="appendOuterIcon"
        :required="required"
        :error="error"
        :prepend-inner-icon="prependInnerIcon"
        :rules="rules"
        :outlined="outlined"
        :dense="dense"
        @click:append-outer="bubbleCustomEvent('click:append-outer', $event)"
        @input="deferInputEvent($event)"
        @focus="bubbleEvent($event)"
        @blur="bubbleEvent($event)"
        @click:clear="bubbleCustomEvent('click:clear', $event)"
        @change="$emit('change', $event)"
        @update:search-input="updateCustomValue($event)"
        @keydown="bubbleEvent($event)"
      >
        <template v-if="showSelectAll && items.length" #prepend-item>
          <v-list-item ripple @click="toggleSelectAll">
            <v-list-item-action>
              <v-icon :class="{ 'primary--text': allItemsSelected }">
                <slot name="select-all-icon">
                  {{ selectAllIcon }}
                </slot>
              </v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title :class="{ 'primary--text': allItemsSelected }">
                <slot name="select-all-text">
                  {{ $m.select_all }}
                </slot>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider />
        </template>

        <template v-else-if="prependItemConfiguration" #prepend-item>
          <v-list-item ripple :to="prependItemConfiguration.location">
            <v-list-item-content>
              <v-list-item-title>
                {{ prependItemConfiguration.text }}
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-icon>
              <v-icon v-if="prependItemConfiguration.icon">
                {{ prependItemConfiguration.icon }}
              </v-icon>
              <v-icon v-else>add</v-icon>
            </v-list-item-icon>
          </v-list-item>
          <v-divider />
        </template>

        <template
          v-if="Array.isArray(value) && value.length"
          #selection="{ item, index }"
        >
          <slot name="selection" v-bind="{ item, index }">
            <span v-if="index === 0">
              {{ itemPropertyText(item) }}
            </span>
            <span v-if="index === 1" class="pl-1">
              (+{{ value.length - 1 }} {{ $m.others.toLocaleLowerCase() }})
            </span>
          </slot>
        </template>

        <!-- Pass through any other slots -->
        <template v-for="(_, slot) of $slots" #[slot]>
          <slot :name="slot" />
        </template>
      </component>
    </template>
  </bm-control>
</template>

<script lang="ts">
import BmControl from "@/components/controls/input/BmControl.vue";
import BmInputField from "@/components/controls/input/mixins/BmInputField";
import type { VuetifyPropertyArg } from "@/components/utils/vuetifyHelpers";
import { getPropertyFromItem } from "@/components/utils/vuetifyHelpers";
import Guid from "@/app/types/common/guid";
import isEqual from "lodash-es/isEqual";
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { Location } from "vue-router";
import userStore from "@/store/modules/userStore";
import type { Comparer } from "@/components/controls/grid/BmGridData";
import dataComponentAttrs from "@/components/controls/DataComponentAttrs";
import type Key from "@/app/types/common/key";

export interface PrependItemConfiguration {
  text: string;
  location: Location;
  icon?: string;
}

function objHasIsHidden(
  obj: Object
): obj is typeof obj & { isHidden: boolean } {
  if ("isHidden" in obj && typeof obj.isHidden === "boolean") {
    return true;
  }

  return false;
}

/**
 * A generic approach to ensuring items correctly get isHidden applied.
 * This is currently expected to work for root level & obj.details.isHidden.
 *
 * I've avoided a recursive obj check for now, as it could cause performance issues
 * or affect items that are unintended to be filtered.
 */
function defaultIsHiddenCheck(item: unknown) {
  // No item, or not an obj
  if (!item || typeof item !== "object") {
    return false;
  }

  // We have isHidden, so use it
  if (objHasIsHidden(item)) {
    return item.isHidden;
  }

  // We have a details obj
  if (
    "details" in item &&
    typeof item.details === "object" &&
    item.details !== null
  ) {
    // Check for is hidden inside the details obj
    if (objHasIsHidden(item.details)) {
      return item.details.isHidden;
    }
  }

  return false;
}

/**
 * Vuetify under the hood requires a primitive 'item-value' on it's selects to function correctly in all cases. As we
 * have many cases where we want to use a Guid object as value this control handles this special case implicitly.
 *
 * Note. Swaps between VCombobox and VAutocomplete backing implementations depending on allowed custom values.
 * VCombobox defaults to "return-object" so explicitly disabled.
 */
export default defineComponent({
  name: "BmDropdown",
  components: { BmControl },
  mixins: [BmInputField],
  inheritAttrs: false,
  props: {
    placeholder: {
      type: String,
      default: " ",
    },
    searchable: {
      type: Boolean,
      default: true,
    },
    allowCustomValue: {
      type: Boolean,
      default: false,
    },
    itemHidden: {
      type: [Object, String, Function] as PropType<VuetifyPropertyArg>,
      default: () => defaultIsHiddenCheck,
    },
    items: {
      type: Array as PropType<readonly any[]>,
      default: () => [],
    },
    itemText: {
      type: [Object, String, Function, Array] as PropType<VuetifyPropertyArg>,
      default: "text",
    },
    itemValue: {
      type: [Object, String, Function, Array] as PropType<VuetifyPropertyArg>,
      default: "value",
    },
    sortItems: {
      type: Boolean,
      default: true,
    },
    sortItemsDesc: {
      type: Boolean,
      default: false,
    },
    returnObject: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [Object, String, Number, Array] as PropType<any>,
      required: false,
    },
    /**
     * Provides a default 'Select All' functionality
     */
    showSelectAll: {
      type: Boolean,
      default: false,
    },
    /**
     * If specified, the dropdown will filter based on the fields specified here rather than the item text
     */
    filterFields: {
      type: Array as PropType<VuetifyPropertyArg[]>,
      default: () => [],
    },
    /**
     * Prepend an item to the start of the dropdown list
     */
    prependItemConfiguration: {
      type: Object as PropType<PrependItemConfiguration>,
      required: false,
    },
    sortFn: {
      type: Function as PropType<Comparer>,
      required: false,
    },
    rules: {
      type: Array as PropType<any[]>,
      required: false,
    },
    readOnly: {
      type: Boolean,
      required: false,
    },
    itemOrder: {
      type: String,
      required: false,
    },
    attach: {
      type: Boolean,
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
    },
    t: {
      type: Object as PropType<Key>,
      required: false,
    },
    multiple: {
      type: Boolean,
      required: false,
    },
    clearable: {
      type: Boolean,
      required: false,
    },
    loading: {
      type: Boolean,
      required: false,
    },
    hideDetails: {
      type: [Boolean, String],
      required: false,
    },
    autoSelectFirst: {
      type: Boolean,
      required: false,
    },
    autocomplete: {
      type: String,
      required: false,
    },
    label: {
      type: String,
      required: false,
    },
    tooltip: {
      type: String,
      required: false,
    },
    errorMessages: {
      type: Array as PropType<string[]>,
      required: false,
    },
    autofocus: {
      type: Boolean,
      required: false,
    },
    routerLocation: {
      type: Object as PropType<Location>,
      required: false,
    },
    appendOuterIcon: {
      type: String,
      required: false,
    },
    required: {
      type: Boolean,
      required: false,
    },
    error: {
      type: Boolean,
      required: false,
    },
    prependInnerIcon: {
      type: String,
      required: false,
    },
    dataQa: {
      type: String,
      required: false,
    },
    outlined: {
      type: Boolean,
      required: false,
    },
    dense: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      internalItemText: "text" as VuetifyPropertyArg,
      itemsInternal: [] as readonly any[],
      dataComponent: dataComponentAttrs.bmDropdown,
      sortedSelectableItems: [] as readonly any[],
    };
  },
  computed: {
    selectableItems(): any[] {
      return this.itemsInternal.filter((item) => {
        const isNotHidden = !getPropertyFromItem(item, this.itemHidden, false);
        return isNotHidden || this.extractPrimitiveValue(item) == this.value;
      });
    },
    /**
     * Returns the component name based on searchable and allowCustomValue bools
     */
    componentType(): any {
      if (this.allowCustomValue) {
        return "VCombobox";
      } else if (this.searchable) {
        return "VAutocomplete";
      }

      return "VSelect";
    },
    /**
     * Generates a friendly string for the current value of the dropdown. For use in read only mode.
     */
    readOnlyValue(): string {
      let value = this.returnObject
        ? getPropertyFromItem(
            this.$props.value,
            this.itemValue,
            this.$props.value
          )
        : this.$props.value;

      if (value !== null && value !== undefined) {
        if (Array.isArray(value)) {
          // if value is an array then we have a multiselect.
          value = this.itemsInternal
            // Find items that have 'values' included in our value array
            .filter((item: any) => {
              const itemValue = getPropertyFromItem(item, this.itemValue, item);
              return value.some(
                (val: any) => val.toString() === itemValue.toString()
              );
            })
            // transform the items into text
            .map((value) =>
              getPropertyFromItem(value, this.internalItemText, value)
            );

          return value.join(", ");
        } else {
          value = this.itemsInternal.find((item: any) => {
            const itemValue = getPropertyFromItem(item, this.itemValue, item);
            return itemValue.toString() === value.toString();
          });

          return this.itemPropertyText(value, ", ");
        }
      }

      return "";
    },
    placeholderVal(): string {
      if (!this.placeholder) {
        return " ";
      }

      return this.placeholder;
    },
    /**
     * Checks if all options within the dropdown have been selected
     */
    allItemsSelected(): any {
      return (
        this.selectableItems.length &&
        this.value?.length === this.selectableItems.length
      );
    },
    /**
     * Updates the select all icon, depending on what has been selected
     */
    selectAllIcon(): any {
      if (this.allItemsSelected) {
        return "check_box";
      }

      if (this.value?.length > 0) {
        return "indeterminate_check_box";
      }

      return "check_box_outline_blank";
    },
    classFromAttrs() {
      return this.$attrs.class;
    },

    primitiveValue() {
      return this.toPrimitiveKey(this.value);
    },
  },
  watch: {
    itemText: {
      // @ts-ignore
      handler: "itemTextWatch",
      immediate: true,
    },
    items: {
      // @ts-ignore
      handler: "itemsWatcherWithRetainSelectAll",
      immediate: true,
      deep: true,
    },
    selectableItems: {
      // @ts-ignore
      handler: "selectableItemsWatcher",
      immediate: true,
      deep: true,
    },
  },
  methods: {
    /**
     * Arrays and functions defined directly in templates are newly created for each render of the parent
     * component. This causes Vue to fire any watchers/computed properties that depend on these values as
     * new references are passed in each time.
     */
    itemTextWatch(
      newItemText: VuetifyPropertyArg,
      oldItemText: VuetifyPropertyArg
    ) {
      if (!oldItemText) {
        this.internalItemText = newItemText;
        return;
      }

      if (
        (newItemText instanceof Function &&
          oldItemText instanceof Function &&
          newItemText.toString() === oldItemText.toString()) ||
        (Array.isArray(newItemText) &&
          Array.isArray(oldItemText) &&
          isEqual(newItemText, oldItemText))
      ) {
        return;
      } else {
        this.internalItemText = newItemText;
      }
    },
    customFilter(item: any, queryText: string, itemText: string): boolean {
      if (this.filterFields.length == 0) {
        // Default V-Autocomplete
        return (
          itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) >
          -1
        );
      } else {
        return this.filterFields.some((fieldRef) => {
          const fieldVal = getPropertyFromItem(item, fieldRef, "");
          const strFieldVal: string =
            fieldVal === undefined ? "" : fieldVal + "";
          return (
            strFieldVal
              .toLocaleLowerCase()
              .indexOf(queryText.toLocaleLowerCase()) > -1
          );
        });
      }
    },
    /**
     * Wrapper of itemPropertyText, to be bound into Vuetify dropdown itemText
     * prop to avoid using a lambda function in the template.
     *
     * VSelect's itemText property expects a function of type:
     *
     * `(item: object, fallback?: any) => any`
     *
     * In this case, the fallback provided by VSelect is the same object as
     * item, so it is just ignored.
     */
    itemPropertyTextBinding(item: object, fallback?: any) {
      return this.itemPropertyText(item);
    },
    itemPropertyText(item: any, separator: string = " | "): string {
      // v-model values are appearing inside our :items when using multiple
      // Ignore them, otherwise our :item-text selectors won't work correctly
      if (item instanceof Array) {
        return "";
      }

      const itemPropertyValue: string | string[] = getPropertyFromItem(
        item,
        this.internalItemText,
        item?.toString() ?? item
      );

      return itemPropertyValue instanceof Array
        ? itemPropertyValue.join(separator)
        : itemPropertyValue;
    },
    /**
     * Extracts primitive value using the itemValue extractor prop
     */
    extractPrimitiveValue(item: any) {
      if (this.returnObject) {
        return item;
      }
      // Perform the key extractions ourselves. Then convert it to a primitive type if required.
      const val = getPropertyFromItem(item, this.itemValue);
      return this.toPrimitiveKey(val);
    },
    propertyCompare(itemA: any, itemB: any): 0 | 1 | -1 {
      return this.itemPropertyText(itemA).localeCompare(
        this.itemPropertyText(itemB),
        userStore.state.userLanguageLocale,
        {
          sensitivity: "base",
        }
      ) as 0 | 1 | -1;
    },
    toPrimitiveKey(item: any): any {
      // return as-is if it's already a primitive
      if (item !== Object(item) || this.returnObject) {
        return item;
      }

      // If it's an array then we need to apply this function to the array contents.
      if (Array.isArray(item)) {
        return item.map((elementKey) => this.toPrimitiveKey(elementKey));
      }

      return item?.toString();
    },
    fromPrimitiveKey(primitiveKey: any): any {
      // If it's an array then we need to apply this function to the array contents.
      if (Array.isArray(primitiveKey)) {
        return primitiveKey.map((elementKey) =>
          this.fromPrimitiveKey(elementKey)
        );
      }

      // return as-is if it's already null/undefined, or non-primitive
      if (!primitiveKey || primitiveKey === Object(primitiveKey)) {
        return primitiveKey;
      }

      // Special case for Guid's (we should extract this out of here at some point)
      const resultGuid = Guid.tryParse(primitiveKey);
      if (resultGuid) {
        return resultGuid;
      }

      return primitiveKey;
    },

    /**
     * Selects, or deselects all items, emitting the updated value
     */
    toggleSelectAll() {
      if (this.allItemsSelected) {
        this.$emit("input", []);
      } else {
        this.$emit(
          "input",
          this.selectableItems.map((item) =>
            getPropertyFromItem(item, this.itemValue)
          )
        );
      }
    },
    /**
     * We currently use implicit "Select All" behaviour, meaning that if all items
     * are selected then we assume the user has explicitly selected all. However,
     * if items are added/removed from the source data we lose the select all
     * state and new items would be excluded which would likely be undesirable.
     *
     * Ideally we should change the implementation to explicit select all, but for
     * now will reselect new items if the previous state had all selected.
     *
     */
    itemsWatcherWithRetainSelectAll() {
      // check it we are currently "Selecting All" on the internal state.
      if (this.showSelectAll && this.allItemsSelected) {
        const oldSelectableItems = this.selectableItems.map((item) =>
          getPropertyFromItem(item, this.itemValue)
        );

        // perform data update
        this.itemsInternal = this.items;

        const newSelectableItems = this.selectableItems.map((item) =>
          getPropertyFromItem(item, this.itemValue)
        );

        // Only emit updated value if we have differences to prevent looping
        if (!isEqual(oldSelectableItems.sort(), newSelectableItems.sort())) {
          // schedule update to selected items.
          this.$nextTick(() => {
            this.$emit("input", newSelectableItems);
          });
        }
      } else {
        // perform data update
        this.itemsInternal = this.items;
      }
    },
    /**
     * Watches the selectable items and sorts them if required. Note: will not
     * react to sortItems and sortItemsDesc prop changes.
     */
    selectableItemsWatcher(
      newSelectableItems: any[],
      oldSelectableItems: any[]
    ) {
      // Only sort if the items have changed - sort is an expensive operation
      // so cannot happen inside a computed.
      if (isEqual(newSelectableItems, oldSelectableItems)) {
        return;
      }

      let comparer: Comparer;

      if (this.sortFn) {
        comparer = this.sortFn;
      } else {
        comparer = (
          !this.sortItemsDesc
            ? this.propertyCompare
            : (a: any, b: any) => this.propertyCompare(a, b) * -1
        ) as Comparer;
      }

      this.sortedSelectableItems = this.sortItems
        ? newSelectableItems.slice().sort(comparer)
        : newSelectableItems;
    },
    /**
     * A watcher on itemText is fired when its value changes in VAutocomplete
     * (base component of VCombobox). The value only changes when a function
     * is passed as an argument (the function doesn't change, so this watcher
     * shouldn't be fired). The watcher runs updateCombobox(), which sets
     * the internal value of VCombobox to null, hence the value of BmDropdown
     * is not updated when a new item is selected from the dropdown.
     *
     * By deferring the input event emit from BmDropdown, this process is
     * skipped over and the correct value from the event is emitted. This is
     * a hacky approach and needs to be looked at in more detail.
     */
    deferInputEvent(event: any) {
      if (
        this.rules?.some((rule: any) => rule.showRequiredIndicator) &&
        (event === null || event === undefined)
      ) {
        return;
      }
      this.allowCustomValue
        ? this.$nextTick(() =>
            this.$emit("input", this.fromPrimitiveKey(event))
          )
        : this.$emit("input", this.fromPrimitiveKey(event));
    },
    /**
     * When operating as a combobox the value entered manually into the dropdown is not emitted as 'input' events on a per
     * key basis, but instead on blur. In some cases we have seen input changes missed as the blur event + input event are
     * missed when closing dialogs.
     *
     * This handler emits input events on a per-key basis when operating as a VCombobox.
     *
     * https://github.com/vuetifyjs/vuetify/issues/4679
     *
     * Update: this issue was fixed when porting VCombobox to Vuetify 3
     * https://github.com/vuetifyjs/vuetify/pull/14791
     *
     * TODO: reinvestigate when migrating to Vue/Vuetify 3
     */
    updateCustomValue(text: string) {
      if (this.allowCustomValue) {
        this.$emit("input", text);
      }
    },
  },
});
</script>
